import {
  type CMSUniversalProductShelfData,
  type CMSProductShelfData,
  type CMSProductShelfCollectionVariant,
} from '@/features/cms/components/CMS/types'
import { Shelf } from '@/features/shop/components/Shelf'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { CMSShelfLayoutContainerConditional } from '@/features/cms/components/CMS/components/Shelves/CMSShelfLayoutContainer'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useCMSLayoutContext } from '@/features/cms/components/CMS/CMSLayoutContext'
import { useCallback, useMemo } from 'react'
import { GlobalRetailerContextProvider } from '@/features/shared/context/RetailerContext'
import { useUserId, useUserZip } from '@/features/account/services/User/hooks'
import { GlobalProductShelf } from '@/features/cms/components/CMS/components/Shelves/ProductShelf/GlobalProductShelf'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'

import { MIN_HEIGHT } from '@shared/constants/productCard'
import { getShelfViewAll } from '@/features/cms/components/CMS/components/Shelves/getShelfViewAll'
import { RefreshProductShelf } from '@/features/cms/components/CMS/components/Shelves/ProductShelf/ProductShelfRefresh/RefreshProductShelf'

export type CMSProductShelfProps = CMSProductShelfData & {
  index: number
  isCollection?: boolean
  categoryIndex?: number
  shelfCategory?: string
  variant?: CMSProductShelfCollectionVariant
}

export const CMSProductShelf = (props: CMSProductShelfProps) => {
  const { retailer_id, metro_id, store_location_id } =
    props.data.store_logo?.data ?? props.data.store_card?.data ?? {}

  const storeParams = {
    store_id: retailer_id ?? null,
    metro_id: metro_id ?? null,
    store_location_id: store_location_id ?? null,
    zip: useUserZip(),
    user_id: useUserId() ?? 0,
  }

  // if store_logo/store_params data exists, we're in a global context, so wrap the shelf accordingly
  if (storeParams.store_id || props.data.store_card?.data.retailer_id) {
    return (
      <GlobalRetailerContextProvider globalStoreParams={storeParams}>
        <CMSProductShelfImpl {...props} linkToUniversalPDP={false} />
      </GlobalRetailerContextProvider>
    )
  }
  return <CMSProductShelfImpl {...props} linkToUniversalPDP={false} />
}

type CMSUniversalProductShelfProps = CMSUniversalProductShelfData & {
  index: number
}
/**
 * This component corresponds to the `universal_product_shelf` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/universal_product_shelf
 * - **Production** - https://cms.shipt.com/content-type/pool/universal_product_shelf
 */
export const CMSUniversalProductShelf = (props: CMSUniversalProductShelfProps) => {
  return (
    <CMSProductShelfImpl
      data={props.data}
      metadata={props.metadata}
      content_type_id={props.content_type_id}
      id={props.id}
      index={props.index}
      name={props.name}
      analytic_slug={props.analytic_slug}
      linkToUniversalPDP
    />
  )
}
type ImplProps = Omit<CMSProductShelfProps, 'content_type_id'> & {
  content_type_id: string
  linkToUniversalPDP: boolean
}
/**
 * This component corresponds to the `product_shelf` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/product_shelf
 * - **Production** - https://cms.shipt.com/content-type/pool/product_shelf
 */
const CMSProductShelfImpl = ({
  data,
  metadata,
  content_type_id,
  id,
  index,
  name,
  analytic_slug,
  isCollection = false,
  categoryIndex,
  shelfCategory,
  linkToUniversalPDP,
  variant,
}: ImplProps) => {
  const adPlacementId = metadata?.read_operation_info?.ad_placement_id
  const {
    items,
    heading,
    view_all_link,
    store_logo,
    subheading,
    hide_ratings,
    display_option,
    store_card,
  } = data

  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const handleViewAll = useCallback(() => {
    if (!view_all_link?.url) return

    trackCMSElementClicked({
      type: 'link',
      content: 'View all',
      // For the shelf-related CMS components, the DS team would like for us to continue sending the shelf's heading/title.
      shelf_name: heading,
      shelf_index: index,
    })
  }, [heading, index, trackCMSElementClicked, view_all_link])

  const viewAll = getShelfViewAll({ viewAllHref: view_all_link?.url, onViewAll: handleViewAll })

  const layout = useCMSLayoutContext()

  const products = useMemo(() => {
    return items?.map((item) => normalizeProduct(item)) ?? []
  }, [items])

  const shelfProducts = useRenderShelfProducts({
    shelfName: heading ?? name,
    shelfIndex: index,
    viewAll,
    products,
    hideRatings: hide_ratings,
    trackingProps: { analyticSlug: analytic_slug, adPlacementId },
    linkToUniversalPDP,
  })

  if ((display_option || isCollection) && variant !== 'stack') {
    return (
      <RefreshProductShelf
        id={`cms-${content_type_id}-${id}`}
        index={index}
        data={data}
        name={name}
        analyticSlug={analytic_slug}
        products={products}
        isCollection={isCollection}
        categoryIndex={categoryIndex}
        shelfCategory={shelfCategory}
        adPlacementId={adPlacementId}
        linkToUniversalPDP={linkToUniversalPDP}
      />
    )
  } else if (store_logo?.data || store_card?.data) {
    return (
      <GlobalProductShelf
        id={`cms-${content_type_id}-${id}`}
        storeId={store_logo?.data.retailer_id ?? store_card?.data.retailer_id ?? null}
        src={store_logo?.data.logo.src || store_card?.data.logo.src}
        heading={heading ?? store_card?.data.name ?? name}
        index={index}
        products={products}
        subheading={subheading?.elements}
        viewAllLink={view_all_link}
        data={data}
        analyticSlug={analytic_slug}
        adPlacementId={adPlacementId}
        linkToUniversalPDP={linkToUniversalPDP}
      />
    )
  } else {
    return (
      <CMSShelfLayoutContainerConditional
        {...getCMSWrapperProps({ content_type_id, id })}
        layout={layout}
      >
        <section>
          <Shelf
            title={heading}
            shelfIndex={index}
            minHeight={MIN_HEIGHT}
            subTitle={subheading?.elements}
            slideSpacing={16}
            {...viewAll}
          >
            {shelfProducts}
          </Shelf>
        </section>
      </CMSShelfLayoutContainerConditional>
    )
  }
}
